import styled from '@emotion/styled';
import React, { useEffect } from 'react';

import type { SeverityLevels } from './types';
import { usePushPageDown } from '../../hooks';
import type { LensAsideProps } from '../../types';
import { u } from '../../utilities';
import Align from '../align/align';
import Arrange from '../arrange/arrange';
import Container from '../container/container';
import { SvgAlertTriangle } from '../icon/available-icons/alert-triangle';
import { SvgClose } from '../icon/available-icons/close';
import { SvgInfo } from '../icon/available-icons/info';
import { SvgWarningAlert } from '../icon/available-icons/warning-alert';
import Icon from '../icon/icon';
import IconButton from '../icon-button/icon-button';

const severityStyles = {
  info: {
    bgColor: 'var(--lns-color-blurple)',
    icon: <SvgInfo />,
    color: 'var(--lns-color-white)',
    fontFamily: 'inherit',
  },
  warning: {
    bgColor: 'var(--lns-color-warning)',
    icon: <SvgWarningAlert />,
    color: 'var(--lns-color-grey8)',
    fontFamily: 'inherit',
  },
  error: {
    bgColor: 'var(--lns-color-danger)',
    icon: <SvgAlertTriangle />,
    color: 'var(--lns-color-white)',
    fontFamily: 'inherit',
  },
  internal: {
    icon: <span role="img">🔔</span>,
    color: 'var(--lns-color-tealLight)',
    bgColor: 'var(--lns-color-grey8)',
    fontFamily: 'var(--lns-fontFamily-code)',
  },
};

const DURATION = 350;
const NotificationBarWrapper = styled.aside<NotificationBarWrapperProps>`
  --paddingXOffset: var(--lns-space-large);
  --alignItems: start;

  display: grid;
  align-items: var(--alignItems);
  justify-content: space-between;
  grid-template-columns: 1fr auto;
  ${props => `background-color: ${severityStyles[props.severity].bgColor}`};
  ${props => `font-family: ${severityStyles[props.severity].fontFamily}`};

  ${props => `color: ${severityStyles[props.severity].color}`};
  position: fixed;
  padding: var(--lns-space-medium) var(--paddingXOffset);
  top: 0;
  left: 0;
  transition:
    ${DURATION}ms box-shadow,
    ${DURATION}ms transform;
  width: 100%;
  box-sizing: border-box;
  z-index: 1100;
  opacity: ${props => (props.isOpen ? '1' : '0')};
  transform: ${props =>
    props.isOpen ? 'translateY(0px)' : 'translateY(-100%)'};
  @media (min-width: 872px) {
    --alignItems: center;
  }
`;

const NotificationBar = ({
  children,
  onCloseClick,
  isOpen,
  severity = 'info',
  id,
}: NotificationBarProps &
  React.ComponentProps<typeof NotificationBarWrapper> &
  Omit<
    LensAsideProps,
    keyof NotificationBarProps &
      React.ComponentProps<typeof NotificationBarWrapper>
  >) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseClick && onCloseClick();
      }
    };

    window.addEventListener('keydown', keyListener);

    return () => {
      window.removeEventListener('keydown', keyListener);
    };
  }, [isOpen, onCloseClick]);

  usePushPageDown(isOpen);

  // NOTE: The UI for internal alert is slightly different.
  const isInternalAlert = severity === 'internal';

  return isOpen ? (
    <NotificationBarWrapper isOpen={isOpen} severity={severity} id={id}>
      <Arrange
        alignItems={{ default: 'start', small: 'center' }}
        justifyContent="space-between"
        autoFlow={isInternalAlert ? 'column' : undefined}
        columns={!isInternalAlert ? ['1fr auto'] : undefined}
      >
        <Container
          paddingY={{ default: 'xsmall', xsmall: 0 }}
          // Visually offsets the close X so that text appears centre-aligned
          paddingLeft={
            !isInternalAlert ? { default: 0, medium: u(3.5) } : undefined
          }
          width="100%"
        >
          <Arrange
            autoFlow="column"
            gap={isInternalAlert ? 'medium' : 'small'}
            justifyContent="center"
          >
            {severityStyles[severity]?.icon ? (
              <Align alignment="topLeft">
                <Icon
                  icon={severityStyles[severity].icon}
                  color={
                    severityStyles[severity].color ?? 'var(--lns-color-white)'
                  }
                />
              </Align>
            ) : null}{' '}
            {children}
          </Arrange>
        </Container>
      </Arrange>
      {onCloseClick && (
        <IconButton
          iconColor={severityStyles[severity].color ?? 'var(--lns-color-white)'}
          tabIndex={0}
          altText="Close"
          icon={<SvgClose />}
          onClick={onCloseClick}
        />
      )}
    </NotificationBarWrapper>
  ) : null;
};

type NotificationBarProps = {
  children?: React.ReactNode;
  isOpen?: boolean;
  onCloseClick?: () => void;
  severity: SeverityLevels;
};

type NotificationBarWrapperProps = {
  children?: React.ReactNode;
  isOpen?: boolean;
  severity: SeverityLevels;
};

export default NotificationBar;
